import cn from 'classnames';
import * as React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import '../../../assets/general-flow/footer.scss';
import {
  Button,
  Col,
  Container,
  IconArrow,
  IconInfo,
  IconPhone,
  QuotePriceDisplay,
  Row,
  SheetLayoutFooter,
} from '../../../brenger-shared-ui';
import { RootState } from '../../../types';
import { getActiveLanguageSettings } from '../../../utils/localization';
import { ContactNotification } from '../components/notifications/ContactNotification';
import { DeferredVatNotification } from '../components/notifications/DeferredVatNotification';
import { ExtraCareGlassNotification } from '../components/notifications/ExtraCareGlass';
import { ExtraCareScooterNotification } from '../components/notifications/ExtraCareScooter';
import { ProductSelectionFloorService } from '../components/notifications/ProductSelectionFloorService';
import { TransportRequestValidationModal } from '../components/TransportRequestValidationModal';
import {
  actions as flowActions,
  getAppliedDiscount,
  getToPaymentButtonTranslation,
  isLoading,
  isQuotePriceDisplayable,
  isSubmitDisabled,
} from '../ducks';

interface OwnProps {
  customSubmitFunction?(): void;
  customIsLoading?: boolean;
  customIsDisabled?: boolean;
}

const FooterLayout: React.FC<ReduxType> = props => {
  const langSettings = getActiveLanguageSettings();
  const withContactBar = ['de-DE'].includes(langSettings.labels.full);

  return (
    <SheetLayoutFooter>
      <Container>
        {props.transport.notifications.visible && (
          <div className={`footer-notification-bar ${props.transport.notifications.type}-notification`}>
            {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
            <p dangerouslySetInnerHTML={{ __html: props.transport.notifications.message }} />
            <span onClick={props.hideNotification}>x</span>
          </div>
        )}
        <div className={cn('relative', { 'notifications--with-contact-bar': withContactBar })}>
          {/* show notification on contact page to tell the user that the price he sees is limited to this day - ABTested */}
          <ContactNotification />
          <DeferredVatNotification />
          <ExtraCareScooterNotification />
          <ExtraCareGlassNotification />
          <ProductSelectionFloorService />
        </div>
        <Row>
          {withContactBar && (
            <div className="sheet-layout--footer-contact">
              <Translate
                id={'request_flow.contact_us_by_phone'}
                data={{
                  phone: (
                    <div style={{ display: 'inline-block' }}>
                      <IconPhone />
                      <a href={`tel:${langSettings.contact.phone.customer}`}>{langSettings.contact.phone.customer}</a>
                    </div>
                  ),
                }}
              />
            </div>
          )}
          <Col xs={5} sm={6} extraClasses={'footer-action-bar min-[992px]:hidden'}>
            {props.isQuotePriceDisplayable && (
              <div
                onClick={props.toggleSheet}
                className={cn('inline-flex items-start gap-2 relative', {
                  'top-1': !props.discount,
                  'top-[-0.5em]': props.discount,
                })}
              >
                <QuotePriceDisplay addOriginalPrice={true} />
                <div style={{ position: 'relative', top: props.discount ? '-0.25em' : '.33em' }}>
                  <IconInfo color="blue" />
                </div>
              </div>
            )}
          </Col>
          <Col xs={7} sm={6} extraClasses={'offset-lg-6'}>
            <Button
              onClick={props.customSubmitFunction}
              dataQaId="continue-general-flow"
              loading={props.isLoading}
              disabled={props.isSubmitDisabled}
              buttonStyle={'secondary'}
              iconPos={'right'}
              fullWidth={true}
            >
              <Translate id={props.nextButtonTrans} />
              <IconArrow />
            </Button>
            <TransportRequestValidationModal />
          </Col>
        </Row>
      </Container>
    </SheetLayoutFooter>
  );
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: RootState, ownProps: OwnProps) => ({
  transport: state.generalTransport,
  isLoading: ownProps.customIsLoading || isLoading(state),
  nextButtonTrans: getToPaymentButtonTranslation(state),
  isSubmitDisabled: ownProps.customIsLoading || (ownProps.customIsDisabled ?? isSubmitDisabled(state)),
  discount: getAppliedDiscount(state),
  isQuotePriceDisplayable: isQuotePriceDisplayable(state),
  customSubmitFunction: ownProps.customSubmitFunction,
});

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapDispatchToProps = (dispatch: Dispatch) => ({
  toggleSheet: () => dispatch(flowActions.toggleSheet()),
  hideNotification: () => dispatch(flowActions.hideNotification()),
});

type ReduxType = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(FooterLayout);
